import { ASSIGNED_SEATING, enums } from "@shared/constants";
import { useAwaitComponent } from "@shared/hooks/useAwaitComponent";
import { PUBLIC_ROUTES } from "@app-contexts/public/constants/public-routes";
import { TicketTypeSerializer } from "@shared/modules/events/types/public";
import { getPrice } from "@shared/modules/financials/utils/price-utils";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import { BestAvailableQueryActions } from "@shared/modules/seating/features/BestAvailable/hooks/useBestAvailableQueryParams";
import { findTicketTypeInfo } from "@shared/modules/seating/features/BestAvailable/utils/utils";
import SeatingService from "@shared/modules/seating/services/SeatingService";
import StageService from "@shared/modules/seating/services/StageService";
import { SeatingItemPropertyMap } from "@shared/modules/seating/types/seating-property-types";
import {
  CartPermissionType,
  FormattedSeatType,
  FormattedSeatWithLocationDataType,
  ItemSelectReturnType,
  ItemsOnSeatType,
  LocationDataType,
  SeatWithLocationDataType,
  SelectGAType,
  SelectSeatType,
} from "@shared/modules/seating/types/seating-types";
import {
  isTicketTypeSoldOut,
  isTicketTypeWaitlisted,
} from "@shared/modules/ticket-types/utils/ticket-type-utils";
import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";
import { useStore } from "@shared/store/useStore";
import { useTranslation } from "@shared/utils/internationalization";
import { useCallback, useState } from "react";
import useRedirect from "@shared/hooks/useRedirect.web";
import { useSeatingAnalytics } from "../useSeatingAnalytics/useSeatingAnalytics";
import useSeatingWaitlistHelpers from "../useSeatingWaitlistHelpers/useSeatingWaitlistHelpers";

const notifier = NotifierFactory.createNotifier();
/**
 * In order for a seat to be waitlisted
 *
 * It needs to have a waitlist object on the item
 *
 * It needs to have no resale ID
 *
 * OR
 *
 * It needs to have a resale ID with a resale status of completed
 *
 * We do this so that completed waitlist/resales are not shown as unavailable
 */
const waitlistResaleCompatibilityCheck = (p) =>
  p.seatingItemResaleId === null ||
  (p.seatingItemResaleId !== null &&
    p.seatingItemResaleStatus ===
      enums.RESALE_SUBMISSION_STATUS_MAP.COMPLETED.id);

function useAssignedSeatingHelpers(allInPricingEnabled?: boolean): {
  onPermissionSelect(
    obj: SelectGAType | SelectSeatType
  ): Promise<void | null> | null;
  removeSeatPermission: (
    permissionId: number,
    itemGroupParentId: number | null
  ) => Promise<void>;
  removeLocationPermission: (
    itemTypeId: number,
    permissionId: number,
    qty: number
  ) => Promise<void>;
  requestUpdateLocationPermissions: (
    locationArray: Array<{
      itemTypeId: number;
      permissionId: number;
      qty: number;
    }>,
    location?: LocationDataType,
    itemGroupId?: number
  ) => Promise<void>;
  requestModifyLocationPermissions(
    locationArray: Array<{
      itemTypeId: number;
      permissionId: number;
      qty: number;
    }>,
    location?: LocationDataType,
    itemGroupParent?: number,
    isWaitlist?: boolean
  ): Promise<void>;
  itemSelectModalOpen: boolean;
  itemsOnSeat: undefined | ItemsOnSeatType;
  resolveItemSelect: (val: unknown) => void;
  rejectItemSelect: (err: unknown) => void;
  updateTicketAuthArray: (
    input: Array<{ id: number; authorized: boolean }>
  ) => void;
  ticketAuthArray: Array<{
    id: number;
    authorized: boolean;
  }>;
  handleSeatSelect: ({
    $data,
    $name,
    $permissions,
    animateSpinner,
    isModifyLevel,
  }: SelectSeatType & { isModifyLevel?: boolean }) => Promise<void | null>;
  getSeatSelection: ({
    $data,
    $name,
    $permissions,
  }: SelectSeatType) => Promise<FormattedSeatType | null>;
  handleGASelect: ({
    $data,
    $name,
    $permissions,
  }: SelectGAType) => Promise<void | null>;
  awaitItemSelectModal(): Promise<
    ItemSelectReturnType | Array<ItemSelectReturnType>
  >;
  getItemTypeInPendingCart: () => SeatingItemPropertyMap | undefined;
  addPendingCartPermissions: (seats: Array<FormattedSeatType>) => void;
  prepareSeatsForMinimumPurchase: (
    itemType: number,
    minPurchaseLimit: number,
    seats: Array<FormattedSeatType>
  ) => Array<FormattedSeatType>;
  handleBestAvailableSeatSelection: (cb?: () => void) => Promise<void>;
  onSegmentSelect: (segmentId: number) => void;
  handleRemoveSeat: (params: {
    $data: SeatWithLocationDataType;
    animateSpinner?: () => void;
    seatToRemove?: FormattedSeatType;
  }) => Promise<void | null>;
} {
  const { t } = useTranslation("seating");

  // array of authorized password-protected items
  // does not persist between page refreshes
  const [ticketAuthArray, setTicketAuthArray] = useState<
    Array<{
      id: number;
      authorized: boolean;
    }>
  >([]);

  const updateTicketAuthArray = (
    input: Array<{
      id: number;
      authorized: boolean;
    }>
  ) => {
    setTicketAuthArray(input);
  };

  const {
    basketType,
    enableTracking,
    stageServiceState,
    seatingServiceState,
    seatingEventData,
    seatingItemArray,
    packageItemGroupParent,
    updateSeatingState,
    synchronizeSeating,
    updateBestAvailableSeatingQueryParams,
    actions: seatingActions,
    disableBestAvailableSeating,
    updateSeatingBasketUpdating,
    paymentType,
    purchaseSourcePlatform,
  } = useSeatingState();

  const { redirect } = useRedirect();
  const navigateToCheckout = useCallback(
    () => redirect(PUBLIC_ROUTES.CHECKOUT.ROOT),
    [redirect]
  );

  const basket = useStore.getState().getBasket(basketType);
  const areAllRenewalItemGroups =
    basket?.item_groups &&
    basket?.item_groups?.length > 0 &&
    basket?.item_groups?.every((group) => group.renewal_member);

  const { trackAddSeat, trackModifyLocation } = useSeatingAnalytics({
    seatingServiceState,
    seatingEventData,
  });

  const {
    removePendingCartPermissions,
    getFullPurchaseRequiredConfirmation,
    getResaleSeatSelections,
    getIsolatedSeatSelections,
    getMinPurchaseSeatSelections,
    requestRemoveSeatPermissions: removeSeatFromMap,
    removeSeatPermission: removeSeatFromCart,
    requestAddSeatPermissions: addSeatToCart,
    requestModifySeatPermissions: modifySeatInCart,
    requestModifyLocationPermissions: modifyLocation,
    requestUpdateLocationPermissions: updateLocation,
    requestAddSeatsAndLocationsToCart,
  } = seatingActions;

  const { addSeatWaitlist } = useSeatingWaitlistHelpers();

  // Multi-item select modal variables
  const {
    status: statusItemSelect,
    execute: executeItemSelect,
    resolve: resolveItemSelect,
    reject: rejectItemSelect,
    reset: resetItemSelect,
  } = useAwaitComponent();
  // Show modal while awaiting
  const itemSelectModalOpen = statusItemSelect === "awaiting";

  async function awaitItemSelectModal(): Promise<
    ItemSelectReturnType | Array<ItemSelectReturnType>
  > {
    try {
      return await executeItemSelect();
    } finally {
      resetItemSelect();
    }
  }

  const [itemsOnSeat, setItemsOnSeat] = useState<ItemsOnSeatType>();

  /**
   * Opens ticket selection modal when:
   * There are multiple permissions on a seat
   * There is 1 permission on a seat and it is password-protected
   * There is a pay what you can configuration
   * The user clicked on a GA location
   */
  async function getSeatSelection({
    $data,
    $name,
    $permissions,
  }: SelectSeatType) {
    // Check for an item-level password on the seat
    const itemHasPassword =
      $permissions &&
      $permissions.length === 1 &&
      $permissions[0].seatingItemPasswordProtected;

    // Used to determine if an item-level waitlist exists on the seat
    const itemHasWaitlist =
      $permissions &&
      $permissions.some(
        (p) =>
          waitlistResaleCompatibilityCheck(p) &&
          seatingServiceState?.itemTypeMap[
            p.seatingItemTypeId as keyof typeof seatingServiceState.itemTypeMap
          ].waitlist
      );

    // Check if item has pwyc configuration
    const itemHasPwycConfig =
      $permissions &&
      $permissions.some((p) => {
        // Check itemTypeMap first (inventory data)
        const itemFromMap =
          seatingServiceState.itemTypeMap[p.seatingItemTypeId]
            ?.pay_what_you_can_configuration;

        // If not found in itemTypeMap, check seatingEvent data (this is for venue data)
        const itemFromEvent = seatingEventData?.tickettype_set?.find(
          (tt: TicketTypeSerializer) => tt.id === p.seatingItemTypeId
        )?.pay_what_you_can_configuration;

        return Boolean(itemFromMap || itemFromEvent);
      });

    if (
      ($permissions && $permissions.length > 1) ||
      itemHasPassword ||
      itemHasWaitlist ||
      itemHasPwycConfig ||
      areAllRenewalItemGroups
    ) {
      /**
       * Permissions are not in cart, so display a
       * modal so user can pick item type
       */
      try {
        // set modal input
        setItemsOnSeat({ $data, $name, $permissions });

        // open modal and wait for it to close
        let itemSelection = await awaitItemSelectModal();

        // format modal data as ItemSelectReturnType
        if (Array.isArray(itemSelection)) {
          itemSelection = itemSelection?.[0];
        }

        const {
          itemTypeId,
          permissionId,
          ticketAuthArray,
          minPurchaseLimit,
          requestAmount,
          payWhatYouCanPrice,
          itemGroupId,
        } = itemSelection;

        // update ticket authorizations if user entered a password
        if (ticketAuthArray) {
          updateTicketAuthArray(ticketAuthArray);
        }

        // return selected seat
        return SeatingService.formatSeatForCart({
          itemTypeId,
          permissionId,
          $data,
          itemGroupParent: packageItemGroupParent,
          minPurchaseLimit:
            seatingServiceState?.overridePurchaseLimits?.min ??
            minPurchaseLimit,
          resaleId: $permissions?.find((p) => p.seatingItemResaleId)
            ?.seatingItemResaleId,
          quantity: requestAmount ?? 1,
          payWhatYouCanPrice,
          itemGroupId,
        });
      } catch (error) {
        // if there is an error, add nothing to the cart
        return null;
      } finally {
        // reset modal values
        setItemsOnSeat(undefined);
      }
    }

    if (!$permissions || $permissions.length === 0) {
      return null;
    }

    const {
      seatingItemTypeId: itemTypeId,
      seatingItemPermissionId: permissionId,
      seatingItemMinPurchaseLimit: minPurchaseLimit,
    } = $permissions[0];

    // return selected seat
    return SeatingService.formatSeatForCart({
      itemTypeId,
      permissionId,
      $data,
      itemGroupParent: packageItemGroupParent,
      minPurchaseLimit:
        seatingServiceState?.overridePurchaseLimits?.min ?? minPurchaseLimit,
      resaleId: $permissions.find((p) => p.seatingItemResaleId)
        ?.seatingItemResaleId,
    });
  }

  /**
   * Adds seats to the pending permissions array
   * Updates the map styling by selecting the added seats
   */
  function addPendingCartPermissions(seats: Array<FormattedSeatType>) {
    // if the number of pending permissions is 0 before any seats are added,
    // enter the limited seat selection mode by setting the stageService field to true
    if (seatingServiceState?.pendingCartPermissionsCount === 0) {
      stageServiceState.isLimitedSeatSelectionEnabled = true;
    }

    // get the seats to add to the pending cart array
    const seatsToAdd = seats.filter(
      (seat) => !seatingServiceState?.getCartPermission(seat.permissionId)
    );

    // select the added seats on the map
    seatsToAdd.map((seat) =>
      stageServiceState?.setLocationSelected(
        { seat: seat.$data },
        true,
        undefined,
        !disableBestAvailableSeating // dont show checkmark with best available seating
      )
    );

    if (seatingServiceState) {
      // update the pending cart array and ticket count
      seatingServiceState.pendingCartPermissions =
        seatingServiceState.pendingCartPermissions.concat(seatsToAdd);
    }

    updateSeatingState();
  }

  /**
   * Attempt to add a seat permission to the cart.
   */
  async function requestAddSeatPermissions(seats: Array<FormattedSeatType>) {
    await addSeatToCart(seats);
    notifier.success(t("Item(s) added to cart!"));
    if (enableTracking) {
      trackAddSeat(basketType, seats);
    }
    updateSeatingState();
  }

  /**
   * Attempt to add a seat permission to the cart.
   */
  async function requestModifySeatPermissions(
    seats: Array<FormattedSeatType>,
    itemGroupId?: number
  ) {
    await modifySeatInCart(seats, itemGroupId);
    notifier.success(t("Item(s) added to cart!"));
    if (enableTracking) {
      trackAddSeat(basketType, seats);
    }
    updateSeatingState();
  }

  /**
   * Attempt to remove seat permission from cart
   * Used on interactions where we don't have seat data
   * example: from clicking the delete icon on the map page's cart
   */
  const removeSeatPermission = async (
    permissionId: number,
    itemGroupParentId: number | null
  ): Promise<void> => {
    // try-catch block is required here, since this function can be called
    // from multiple seating components
    try {
      await removeSeatFromCart(
        permissionId,
        itemGroupParentId,
        seatingEventData
      );
      updateSeatingState();
    } catch (error) {
      // do nothing, the error toast is handled by the repository
    }
  };

  /**
   * Removes a seat from the cart in cases where we have seat data
   * example: from clicking the selected seat node on the map
   */
  async function requestRemoveSeatPermissions(seats: Array<FormattedSeatType>) {
    await removeSeatFromMap(seats);

    // handle toast for successful removal
    notifier.success(t("Item(s) removed from cart"));
    updateSeatingState();
  }

  /**
   * Check if seats in the pending cart array are ready to be added to the cart.
   *
   * Returns an array of seats depending on if the selected seats have reached the minimum purchase limit
   * If the number of seats is still under the limit, add seats to pendingCartPermissions
   * If the limit is reached, add all pending seats to the cart at once
   * If the number of seats is above the limit, add seats to the cart
   */
  function prepareSeatsForMinimumPurchase(
    itemType: number,
    minPurchaseLimit: number,
    inputSeats: Array<FormattedSeatType>
  ): Array<FormattedSeatType> {
    const seatNotInCartPermissions = !seatingServiceState?.cartPermissions.find(
      (s: CartPermissionType) => s.itemTypeId === itemType && !s.location
    );

    // if seats with a minimum purchase limit are selected and the item type isn't already in the cart,
    // handle logic for adding seats to the pending permissions or the cart (if the minimum limit is reached)
    if (seatingServiceState && seatNotInCartPermissions) {
      // add seats to the pending cart array
      addPendingCartPermissions(inputSeats);

      // seats in cart with best available flow
      const bestAvailableSeatsInCartCount =
        seatingServiceState.seatingObjectArray.filter(
          (so) => so.ticketTypeId === itemType
        ).length;

      // if the number of seats is below the minimum purchase limit, don't add them to the cart
      if (
        bestAvailableSeatsInCartCount === 0 &&
        seatingServiceState.pendingCartPermissionsCount < minPurchaseLimit
      ) {
        return [];
      }

      // else the minimum limit is reached, so add seats to the cart
      const updatedSeats = seatingServiceState.pendingCartPermissions;
      // reset the pending permissions array
      seatingServiceState.pendingCartPermissions = [];
      // once all the pending seats are added, exit the limited seat selection mode
      stageServiceState.isLimitedSeatSelectionEnabled = false;
      return updatedSeats;
    }
    // add seats to the cart
    return inputSeats;
  }

  async function handleRemoveSeat({
    $data,
    animateSpinner,
    seatToRemove,
  }: {
    $data: SeatWithLocationDataType;
    animateSpinner?: () => void;
    seatToRemove?: FormattedSeatType;
  }): Promise<void | null> {
    // check if seat has a permission in cart
    const inCartPermission = seatingServiceState?.getSeatInCartPermissions(
      $data.id
    );

    // check if seat has a pending permission in the pending cart array
    const inPendingCartPermission =
      seatingServiceState?.getSeatInPendingCartPermissions($data.id);
    const inSelectSeatIds = seatingServiceState?.seatingObjectArray.some(
      (seat) => seat.id === $data.id
    );
    let returnedPermission = inCartPermission ?? inPendingCartPermission;

    if (inSelectSeatIds && seatToRemove) {
      returnedPermission = seatToRemove;
    }

    let minPurchaseLimit;

    // override the item-level minimum purchase limit
    // example: minimum limit defined on a dynamic hold
    if (seatingServiceState?.overridePurchaseLimits?.min) {
      minPurchaseLimit = seatingServiceState?.overridePurchaseLimits?.min;
    }
    // else use the minimum purchase limit defined on the item type
    else if (returnedPermission && returnedPermission.itemTypeId) {
      minPurchaseLimit =
        seatingServiceState?.itemTypeMap[returnedPermission.itemTypeId]
          ?.minimum_purchase_limit;
    }

    const hasMinPurchaseSeatsInCart: boolean =
      Boolean(minPurchaseLimit) && Boolean(inCartPermission);

    const seat: FormattedSeatType =
      inSelectSeatIds && seatToRemove
        ? seatToRemove
        : SeatingService.formatSeatForCart({
            itemTypeId: returnedPermission?.itemTypeId,
            permissionId: returnedPermission?.permissionId,
            $data: returnedPermission?.seat,
            itemGroupParent: packageItemGroupParent,
            minPurchaseLimit,
            resaleId: returnedPermission?.resaleId,
          });

    const hasResaleItem = Boolean(seat.resaleId);

    let seats: Array<FormattedSeatType> = [seat];

    if (hasResaleItem) {
      const resaleSeats = await getResaleSeatSelections({
        originalSeat: seat,
        resaleId: seat.resaleId,
        remove: true,
      });
      // return null when the modal is cancelled
      if (resaleSeats === null) {
        return null;
      }
      seats = seats.concat(resaleSeats);
    }

    // if min purchase items are in the actual cart, display a prompt to confirm removal
    if (
      hasMinPurchaseSeatsInCart &&
      !hasResaleItem &&
      !seatingServiceState?.isGroupSalesMap()
    ) {
      const minPurchaseSeats = await getMinPurchaseSeatSelections({
        seat,
        inCartPermission,
        minPurchaseLimit,
        itemGroupParent: packageItemGroupParent ?? undefined,
      });

      // return null when the modal is cancelled
      if (minPurchaseSeats === null) {
        return null;
      }

      seats = minPurchaseSeats;
    }

    // if isolated seats are disabled for the event, display a prompt to confirm removal
    if (
      seatingEventData?.no_isolated_seats &&
      !hasMinPurchaseSeatsInCart &&
      !hasResaleItem
    ) {
      const isolatedSeats = await getIsolatedSeatSelections({
        originalSeat: seat,
        remove: true,
      });

      // return null when the modal is cancelled
      if (isolatedSeats === null) {
        return null;
      }
      seats = seats.concat(isolatedSeats);
    }

    // if the seat is part of a location that requires a full purchase, display a prompt to confirm removal
    if (
      $data?.locationData?.full_purchase_required &&
      $data?.locationData?.seats?.length > 1 &&
      !hasMinPurchaseSeatsInCart &&
      !hasResaleItem
    ) {
      const fullPurchaseRequiredSeats =
        await getFullPurchaseRequiredConfirmation($data, true);
      // return null when the modal is cancelled
      if (fullPurchaseRequiredSeats === null) {
        return null;
      }
      seats = seats.concat(fullPurchaseRequiredSeats);
    }

    // remove seats from the selected seat ids array and seating object array
    // This handles best available seating removals
    if (seatingServiceState?.seatingObjectArray.length > 0) {
      const allItemsInSeatingObjectArray = seats.every((seat) =>
        seatingServiceState?.seatingObjectArray.some(
          (s) => s.id === seat.$data.id
        )
      );
      seats.forEach((seat) => {
        seatingServiceState.removeSeatFromSeatingObjectArray(seat.$data.id);
        stageServiceState.removeSeatSelection(seat.$data.id);
      });

      synchronizeSeating();
      // if all items are in the seating object array, return null
      // when this is false, it means there are items in the cart which are not in the seating object array,
      // so the user is removing from their basket
      if (allItemsInSeatingObjectArray) {
        return null;
      }
    }

    // remove seats from the pending cart permissions array
    if (minPurchaseLimit && inPendingCartPermission) {
      removePendingCartPermissions(seats);
      return null;
    }

    // animate seat spinner while waiting for request to resolve
    // skip animation on android
    if (!StageService.isAndroidDevice() && animateSpinner) {
      animateSpinner();
    }

    // remove seats from basket
    return requestRemoveSeatPermissions(seats);
  }

  async function handleSeatSelection({
    $data,
    $name,
    $permissions,
    animateSpinner,
    isModify = false,
    isModifyLevel = false,
  }: SelectSeatType & { isModify?: boolean; isModifyLevel?: boolean }): Promise<
    void | null
  > {
    // opens item selector dialog if there are multiple permissions
    const seat: FormattedSeatType | null = await getSeatSelection({
      $data,
      $name,
      $permissions,
    });

    // return null if the seat selection modal is cancelled
    if (seat === null) {
      return null;
    }

    // Used to determine whether to add a waitlist or normal ticket to the basket
    const hasWaitlist =
      $permissions &&
      $permissions.some(
        (p) =>
          waitlistResaleCompatibilityCheck(p) &&
          seatingServiceState?.itemTypeMap[
            p.seatingItemTypeId as keyof typeof seatingServiceState.itemTypeMap
          ].waitlist
      );

    const hasResaleItem = Boolean(seat.resaleId);

    let seats: Array<FormattedSeatType> = [seat];

    // if the seat is waitlisted, add it to the cart right away
    // we don't need any of the other seat selection modals to open
    if (hasWaitlist && !isModify) {
      // animate seat spinner while waiting for request to resolve
      // skip animation on android
      if (!StageService.isAndroidDevice() && animateSpinner) {
        animateSpinner();
      }
      return addSeatWaitlist(seats);
    }

    // if the seat has a resale item, display a prompt to confirm adding the resale seat(s)
    // if resale seats are selected, add them to the cart right away
    // we don't need any of the other seat selection modals to open
    if (hasResaleItem && !isModify) {
      const resaleSeats = await getResaleSeatSelections({
        originalSeat: seat,
        resaleId: seat.resaleId,
        remove: false,
      });
      // return null when the modal is cancelled
      if (resaleSeats === null) {
        return null;
      }
      seats = resaleSeats;

      // animate seat spinner while waiting for request to resolve
      // skip animation on android
      if (!StageService.isAndroidDevice() && animateSpinner) {
        animateSpinner();
      }
      if (disableBestAvailableSeating) {
        return requestAddSeatPermissions(seats);
      }
    }

    // if isolated seats are disabled for the event, display a prompt to confirm adding the isolated seat(s)
    if (seatingEventData?.no_isolated_seats) {
      const isolatedSeats = await getIsolatedSeatSelections({
        originalSeat: seat,
        remove: false,
      });
      // return null when the modal is cancelled
      if (isolatedSeats === null) {
        return null;
      }
      seats = seats.concat(isolatedSeats);
    }

    // If the seat is part of a location that requires a full purchase, display a prompt to confirm
    if (
      $data?.locationData?.full_purchase_required &&
      $data?.locationData?.seats?.length > 1
    ) {
      const fullPurchaseRequiredSeats =
        await getFullPurchaseRequiredConfirmation($data, false);
      // return null when the modal is cancelled
      if (fullPurchaseRequiredSeats === null) {
        return null;
      }
      seats = seats.concat(fullPurchaseRequiredSeats);
    }

    // if the minimum purchase limit is reached, add all seats to the cart at once
    if (
      seat.minPurchaseLimit &&
      !seatingServiceState?.isGroupSalesMap() &&
      !isModify
    ) {
      seats = prepareSeatsForMinimumPurchase(
        seat.itemTypeId,
        seat.minPurchaseLimit,
        seats
      );
      // return null when number of seats is below minimum purchase limit
      if (seats.length === 0) {
        return null;
      }
    }

    // animate seat spinner while waiting for request to resolve
    // skip animation on android
    if (!StageService.isAndroidDevice() && animateSpinner) {
      animateSpinner();
    }

    const arguements =
      areAllRenewalItemGroups && (seat.itemGroupId || isModifyLevel)
        ? {
            seats,
            itemGroupId:
              basket?.item_groups?.find(
                (group) => group?.permissions?.[0]?.seat?.id === $data.id
              )?.id || seat.itemGroupId,
          }
        : { seats };
    if (!disableBestAvailableSeating && !areAllRenewalItemGroups) {
      const ticketType = findTicketTypeInfo(
        seatingEventData,
        seat as FormattedSeatWithLocationDataType,
        seatingItemArray
      );
      seats.forEach((seat) => {
        const seatData = {
          seatIdentifiers: [seat.$data.identifier],
          segmentIdentifier: seat.$data.segment_identifier,
          locationIdentifier: seat.$data.location_identifier,
          /**
           * Format seat data to be compatible with both best available and individual seat selection.
           * Using key/value pair structure in the maps ensures consistent handling in seating visualization,
           * regardless of selection method (best available or individual selection).
           */
          permissionsMap: {
            [seat.$data.id]: seat.permissionId,
          },
          ticketTypesMap: {
            [seat.$data.id]: seat.itemTypeId,
          },
          pricingMap: {
            [seat.$data.id]: `${getPrice(
              ticketType,
              purchaseSourcePlatform,
              paymentType,
              seatingEventData?.venue?.all_in_pricing_is_enabled ??
                allInPricingEnabled,
              seat.payWhatYouCanPrice !== undefined
                ? parseFloat(seat.payWhatYouCanPrice)
                : undefined
            )}`,
          },
          membershipLevelsMap: {
            [seat.$data.id]: seat.itemTypeId,
          },
          locationData: seat.$data.locationData,
          seatIds: [seat.$data.id],
        };

        // Create a callback that receives the seat ID as a parameter
        const seatSpecificCallback = async (
          seatingObject?: SeatWithLocationDataType
        ) => {
          // Use the seatingObject parameter (the actual clicked seat) instead of the closure seat
          if (seatingObject) {
            await handleRemoveSeat({
              $data: seatingObject,
              seatToRemove: {
                $data: seatingObject,
                permissionId: seat.permissionId,
                itemTypeId: seat.itemTypeId,
                resaleId: seat.resaleId,
                itemGroupParent: seat.itemGroupParent,
                minPurchaseLimit: seat.minPurchaseLimit,
                payWhatYouCanPrice: seat.payWhatYouCanPrice,
              },
            });
          } else {
            // Fallback to closure seat if seatingObject is not provided
            await handleRemoveSeat({
              $data: seat.$data,
              seatToRemove: seat,
            });
          }
        };

        stageServiceState.handleSeatingVisualization({
          seats: seatData,
          selectedIndividually: true,
          cb: seatSpecificCallback,
        });
      });
      // Synchronize React state after adding seats to the service state
      synchronizeSeating();
      return null;
    }
    return isModify
      ? requestModifySeatPermissions(arguements.seats, arguements.itemGroupId)
      : requestAddSeatPermissions(arguements.seats);
  }

  async function handleModifySeatSelection({
    $data,
    $name,
    $permissions,
    animateSpinner,
    isModifyLevel,
  }: SelectSeatType & { isModifyLevel?: boolean }): Promise<void | null> {
    return handleSeatSelection({
      $data,
      $name,
      $permissions,
      animateSpinner,
      isModify: true,
      isModifyLevel,
    });
  }

  async function handleAddSeat({
    $data,
    $name,
    $permissions,
    animateSpinner,
  }: SelectSeatType): Promise<void | null> {
    return handleSeatSelection({
      $data,
      $name,
      $permissions,
      animateSpinner,
      isModify: false,
    });
  }

  /**
   * Function called from clicking seats on the map
   */
  async function handleSeatSelect({
    $data,
    $name,
    $permissions,
    animateSpinner,
    isModifyLevel = false,
  }: SelectSeatType & { isModifyLevel?: boolean }): Promise<void | null> {
    // start processing request
    if (seatingServiceState && !seatingServiceState.isProcessing()) {
      // check if seat has a permission in cart
      const inCartPermission = seatingServiceState.getSeatInCartPermissions(
        $data.id
      );
      // check if seat has a pending permission in the pending cart array
      const inPendingCartPermission =
        seatingServiceState.getSeatInPendingCartPermissions($data.id);

      const inSelectSeatIds = seatingServiceState.seatingObjectArray.some(
        (seat) => seat.id === $data.id
      );
      // Remove seat from cart/pending array in either of these cases:
      // 1. When not in modify selection mode
      // 2. When in modify selection mode but isModifyLevel is false
      // Only skip removal when specifically in modify mode AND modifying a level
      if (
        (inCartPermission || inPendingCartPermission || inSelectSeatIds) &&
        !(seatingServiceState.isModifySeatSelectionMap() && isModifyLevel)
      ) {
        return handleRemoveSeat({
          $data,
          animateSpinner,
          seatToRemove: inSelectSeatIds
            ? { $data, permissionId: Number($data.permissionId) }
            : undefined,
        });
      }

      // if the seating map is in modify seat selection mode, use modify seat selection logic
      if (seatingServiceState.isModifySeatSelectionMap()) {
        return handleModifySeatSelection({
          $data,
          $name,
          $permissions,
          animateSpinner,
          isModifyLevel,
        });
      }
      // add the seat to the cart or pending cart array
      return handleAddSeat({
        $data,
        $name,
        $permissions,
        animateSpinner,
      });
    }
    return undefined;
  }

  /**
   * Attempt to add a location permission to the cart.
   */
  async function requestModifyLocationPermissions(
    locationArray: Array<{
      itemTypeId: number;
      permissionId: number;
      qty: number;
    }>,
    location?: LocationDataType,
    itemGroupParent?: number,
    isWaitlist?: boolean
  ) {
    await modifyLocation(locationArray, location, itemGroupParent, isWaitlist);

    const basket = useStore.getState().getBasket(basketType);

    // display different notifier message for waitlists
    notifier.success(
      basket?.is_waitlist
        ? t("Waitlist selection(s) have been modified!")
        : t("Item selection(s) have been modified!")
    );
    if (enableTracking) {
      trackModifyLocation(basketType, locationArray);
    }
    updateSeatingState();
  }

  async function requestUpdateLocationPermissions(
    locationArray: Array<{
      itemTypeId: number;
      permissionId: number;
      qty: number;
    }>,
    location?: LocationDataType,
    itemGroupId?: number
  ) {
    await updateLocation(locationArray, location, itemGroupId);
    updateSeatingState();
  }

  /**
   * Attempt to remove location permission from cart
   * Used on interactions where we don't have location data
   * example: from clicking the delete icon on the map page's cart
   */
  const removeLocationPermission = async (
    itemTypeId: number,
    permissionId: number,
    qty: number
  ): Promise<void> => {
    // try-catch block is required here, since this function can be called
    // from multiple seating components
    try {
      await modifyLocation([{ itemTypeId, permissionId, qty }], undefined);
      updateSeatingState();
    } catch (error) {
      // do nothing, the error toast is handled by the repository
    }
  };

  /**
   * Function called from clicking GA sections on the map
   */
  const handleGASelect = async ({
    $data,
    $name,
    $permissions,
  }: SelectGAType): Promise<void | null> => {
    if (!seatingServiceState?.isProcessing()) {
      // Skip the ticket type selection modal entirely if:
      // there is only a single ticket type available for the area
      // the minimum and maximum for that single ticket type is the same

      if ($permissions && $permissions.length === 1) {
        const itemTypeId = $permissions[0].seatingItemTypeId;
        const itemTypeObj = seatingItemArray?.find(
          (item) => item.id === itemTypeId
        );

        // also skip modal if:
        // item is not password protected,
        // min & max limits are the same,
        // limits are the same as the area capacity as defined in the venue layout
        if (
          itemTypeObj &&
          !itemTypeObj.is_password_protected &&
          itemTypeObj.minimum_purchase_limit === itemTypeObj.purchase_limit &&
          itemTypeObj.minimum_purchase_limit === $data.capacity
        ) {
          // usage remaining will always either be minimum limit or zero at this point (because limit and capacity are the same), so
          // we can use it to determine the removal or addition of seats
          const requestAmount = $permissions[0].usage_remaining
            ? itemTypeObj.minimum_purchase_limit
            : 0;
          const permissionId = $permissions[0].seatingItemPermissionId;
          // if sold out but has waitlist, then we need to add to waitlist
          const isWaitlist =
            isTicketTypeWaitlisted(itemTypeObj) &&
            isTicketTypeSoldOut(itemTypeObj);

          const locationArray = [
            {
              itemTypeId: itemTypeObj.id,
              permissionId,
              qty: requestAmount,
            },
          ];

          if (!disableBestAvailableSeating) {
            stageServiceState.removeAllBestAvailableGaSelections();
            const price = getPrice(
              itemTypeObj as TicketTypeSerializer,
              purchaseSourcePlatform,
              paymentType,
              Boolean(allInPricingEnabled)
            );

            const obj = {
              permissionIds: [locationArray[0].permissionId],
              itemTypeIds: [locationArray[0].itemTypeId],
              qty: locationArray[0].qty,
              name: $name,
              id: $data.id,
              segment_identifier: $data.section || "",
              identifier: $data.identifier,
              price: [price.toString()],
            };
            stageServiceState.handleGaVisualization({
              formattedSelections: obj,
              data: $data,
            });
            synchronizeSeating();
            return null;
          }

          return requestModifyLocationPermissions(
            locationArray,
            $data,
            packageItemGroupParent,
            isWaitlist
          );
        }
      }

      /**
       * Permissions are not in cart, so display a
       * modal so user can pick item type
       */
      try {
        // set modal input
        setItemsOnSeat({ $data, $name, $permissions });

        // wait for the modal to be dismissed
        let gaSelections = await awaitItemSelectModal();

        // format modal data as Array<ItemSelectReturnType>
        if (!Array.isArray(gaSelections)) {
          gaSelections = [gaSelections];
        }

        const itemTypeIdArray = gaSelections.map((loc) => loc.itemTypeId);
        const itemTypeObjs = seatingItemArray?.filter((item) =>
          itemTypeIdArray.includes(item.id)
        );

        // if sold out but has waitlist, then we need to add to waitlist
        const isWaitlist = itemTypeObjs?.some(
          (obj) => isTicketTypeWaitlisted(obj) && isTicketTypeSoldOut(obj)
        );

        // update ticket authorizations if user entered a password
        if (ticketAuthArray) {
          updateTicketAuthArray(ticketAuthArray);
        }

        const formattedSelections = gaSelections.map((loc) => {
          return {
            ...loc,
            qty: loc.requestAmount ?? 0,
          };
        });

        if (!disableBestAvailableSeating) {
          stageServiceState.removeAllBestAvailableGaSelections();

          const gaVisualizations = formattedSelections.map((selection) => {
            const currentItemType = itemTypeObjs?.find(
              (item) => item.id === selection.itemTypeId
            ) as TicketTypeSerializer;

            const payWhatYouCanPrice = formattedSelections.find(
              (item) => item.itemTypeId === currentItemType.id
            )?.payWhatYouCanPrice;

            const price = getPrice(
              currentItemType,
              purchaseSourcePlatform,
              paymentType,
              Boolean(allInPricingEnabled),
              parseFloat(payWhatYouCanPrice)
            );

            return {
              permissionIds: [selection.permissionId],
              itemTypeIds: [selection.itemTypeId],
              qty: selection.qty,
              name: $name,
              id: $data.id,
              segment_identifier: $data.section || "",
              identifier: $data.identifier,
              price: [price?.toString()],
            };
          });

          gaVisualizations.forEach((obj) => {
            stageServiceState.handleGaVisualization({
              formattedSelections: obj,
              data: $data,
            });
          });

          synchronizeSeating();
          return null;
        }

        return requestModifyLocationPermissions(
          formattedSelections,
          $data,
          packageItemGroupParent,
          isWaitlist
        );
      } catch (error) {
        // catch in this case is when the modal is canceled.
        return null;
      } finally {
        // reset modal values
        setItemsOnSeat(undefined);
      }
    }
    return undefined;
  };

  /**
   * Called when a seat or location is clicked on the map
   * Handles item selection for both node types
   */
  function onPermissionSelect(
    obj: SelectGAType | SelectSeatType
  ): Promise<void | null> | null {
    if (obj.$name === ASSIGNED_SEATING.GENERAL_ADMISSION.ID) {
      return handleGASelect(obj as SelectGAType);
    }
    if (obj.$name === ASSIGNED_SEATING.SEAT.ID) {
      return handleSeatSelect(obj as SelectSeatType);
    }
    return null;
  }

  /*
   * Returns information for the item type currently in the SeatingService's pending cart array
   */
  function getItemTypeInPendingCart(): SeatingItemPropertyMap | undefined {
    let pendingItemType;

    const pendingItemId =
      seatingServiceState?.pendingCartPermissions[0]?.itemTypeId;
    if (pendingItemId) {
      pendingItemType = seatingServiceState?.itemTypeMap[pendingItemId];
    }

    if (!pendingItemId || !pendingItemType) {
      return undefined;
    }

    return pendingItemType;
  }

  async function handleBestAvailableSeatSelection(
    overrideCheckoutRedirect?: () => void
  ): Promise<void> {
    // TODO instead of having this checkout redirect in here, just conditionally call this function
    // in the first place
    function completeSelection() {
      if (overrideCheckoutRedirect) {
        overrideCheckoutRedirect();
        updateSeatingBasketUpdating(false);
      } else {
        navigateToCheckout();
      }
    }

    updateSeatingBasketUpdating(true);
    const selectedSeats = seatingServiceState.getSeatingObjectArray();
    const gaLocations = selectedSeats.filter(
      (seat) => seat.name === ASSIGNED_SEATING.GENERAL_ADMISSION.ID
    );
    const seats = selectedSeats.filter(
      (seat) => seat.name !== ASSIGNED_SEATING.GENERAL_ADMISSION.ID
    );

    const seatDataArray = (
      await Promise.all(
        seats.map(async (seat) => {
          const seatData = await stageServiceState.getSeatData(seat.id, seat);
          const allowedPermissionsOnSeat =
            seatingServiceState?.seatingMap[seat.id];

          // Skip if no permissions or seat data found
          if (!allowedPermissionsOnSeat || !seatData) {
            return null;
          }

          const permissions = allowedPermissionsOnSeat.map((permission) => ({
            ...permission,
            seatingItemType:
              seatingServiceState?.itemTypeMap[permission.seatingItemTypeId],
          }));

          // Only process seats with exactly one permission for now; once multi ticket is implemented,
          // we will change it to check for multiple permissions
          // Multi select is working for individual seating but not for best available seating as of yet
          let selectedPermission;
          if (seat.permissionId) {
            selectedPermission = seat.permissionId
              ? permissions.find(
                  (permission) =>
                    permission.seatingItemPermissionId === seat.permissionId
                )
              : undefined;
          } else {
            const [firstPermission] = permissions;
            selectedPermission = firstPermission;
          }
          if (!selectedPermission) {
            return null;
          }

          // Check if this ticket type has PWYC configuration
          const itemFromMap =
            seatingServiceState?.itemTypeMap[
              selectedPermission.seatingItemTypeId
            ]?.pay_what_you_can_configuration;
          const itemFromEvent = seatingEventData?.tickettype_set?.find(
            (tt: TicketTypeSerializer) =>
              tt.id === selectedPermission.seatingItemTypeId
          )?.pay_what_you_can_configuration;

          const hasPwycConfig = Boolean(itemFromMap || itemFromEvent);

          return SeatingService.formatSeatForCart({
            itemTypeId: selectedPermission.seatingItemTypeId,
            permissionId: selectedPermission.seatingItemPermissionId,
            $data: seatData,
            itemGroupParent: packageItemGroupParent,
            minPurchaseLimit: selectedPermission.seatingItemMinPurchaseLimit,
            resaleId: selectedPermission.seatingItemResaleId,
            ...(hasPwycConfig ? { payWhatYouCanPrice: seat.price } : null),
          });
        })
      )
    ).filter(Boolean) as FormattedSeatType[];

    const gaDataArray = gaLocations
      .map((seat) => {
        if (seat.name === ASSIGNED_SEATING.GENERAL_ADMISSION.ID) {
          // Check if this ticket type has PWYC configuration
          const itemFromMap =
            seatingServiceState?.itemTypeMap[seat.ticketTypeId]
              ?.pay_what_you_can_configuration;
          const itemFromEvent = seatingEventData?.tickettype_set?.find(
            (tt: TicketTypeSerializer) => tt.id === seat.ticketTypeId
          )?.pay_what_you_can_configuration;

          const hasPwycConfig = Boolean(itemFromMap || itemFromEvent);

          return {
            itemTypeId: seat.ticketTypeId,
            permissionId: seat.permissionId,
            qty: seat.qty,
            location: seat.data,
            ...(hasPwycConfig ? { payWhatYouCanPrice: seat.price } : null),
          };
        }
        return null;
      })
      .filter(Boolean);

    if (seatDataArray.length === 0 && gaDataArray.length === 0) {
      completeSelection();
      return;
    }

    try {
      await requestAddSeatsAndLocationsToCart({
        seats: seatDataArray,
        locations: gaDataArray,
        itemGroupParent: packageItemGroupParent,
      });
      // Track analytics for successful seat/location additions
      if (enableTracking) {
        const trackingPromises = [];
        if (seatDataArray.length > 0) {
          trackingPromises.push(trackAddSeat(basketType, seatDataArray));
        }
        if (gaDataArray.length > 0) {
          trackingPromises.push(trackModifyLocation(basketType, gaDataArray));
        }
        // Wait for all tracking to complete before calling completeSelection
        await Promise.allSettled(trackingPromises);
      }
      completeSelection();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to add seat permissions:", error);
      updateSeatingBasketUpdating(false);
    }
  }

  function onSegmentSelect(segmentId: number): void {
    updateBestAvailableSeatingQueryParams({
      type: BestAvailableQueryActions.SET_SEGMENT_ID,
      payload: { segment_id: segmentId },
    });
  }

  return {
    onPermissionSelect,
    removeLocationPermission,
    requestModifyLocationPermissions,
    requestUpdateLocationPermissions,
    removeSeatPermission,
    itemSelectModalOpen,
    resolveItemSelect,
    rejectItemSelect,
    itemsOnSeat,
    ticketAuthArray,
    updateTicketAuthArray,
    handleSeatSelect,
    handleRemoveSeat,
    handleGASelect,
    getSeatSelection,
    awaitItemSelectModal,
    getItemTypeInPendingCart,
    addPendingCartPermissions,
    prepareSeatsForMinimumPurchase,
    handleBestAvailableSeatSelection,
    onSegmentSelect,
  };
}

export default useAssignedSeatingHelpers;
